// src/ImpactRecordPage.js
//
// UPDATED to full CRUD. Kept the expand/collapse card layout from the
// read-only version. Uses beneficiary_label/activity_label/
// recorded_by_label (the new consistent naming) for display.

import { useState, useEffect } from 'react';
import { API_BASE_URL } from './apiConfig';
import ImpactRecordForm from './ImpactRecordForm';

function ImpactRecordPage({ token }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  const refreshRecords = () => {
    fetch(`${API_BASE_URL}/api/impact-records/`, {
      headers: { Authorization: `Token ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) setRecords(data);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refreshRecords();
  }, [token]);

  const toggleExpand = (id) => {
    setExpandedId((current) => (current === id ? null : id));
  };

  const handleNewClick = () => { setEditingRecord(null); setShowForm(true); };
  const handleEditClick = (r) => { setEditingRecord(r); setShowForm(true); };
  const handleSaved = () => { setShowForm(false); setEditingRecord(null); refreshRecords(); };
  const handleCancel = () => { setShowForm(false); setEditingRecord(null); };

  const handleDelete = async (r) => {
    const confirmed = window.confirm('Delete this impact record? This cannot be undone.');
    if (!confirmed) return;
    await fetch(`${API_BASE_URL}/api/impact-records/${r.id}/`, {
      method: 'DELETE',
      headers: { Authorization: `Token ${token}` },
    });
    refreshRecords();
  };

  if (loading) return <p className="text-gray-500">Loading impact records...</p>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Impact Records</h1>
        <button onClick={handleNewClick} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
          + New Impact Record
        </button>
      </div>

      {showForm && (
        <ImpactRecordForm token={token} record={editingRecord} onSaved={handleSaved} onCancel={handleCancel} />
      )}

      <div className="space-y-3">
        {records.map((record) => (
          <div key={record.id} className="bg-white rounded shadow p-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="font-semibold">{record.beneficiary_label}</p>
                <p className="text-sm text-gray-500">
                  {record.activity_label} - recorded by {record.recorded_by_label || 'Unknown'}
                </p>
              </div>
              <div className="flex gap-3 items-center">
                <button onClick={() => toggleExpand(record.id)} className="text-blue-600 text-sm hover:underline">
                  {expandedId === record.id ? 'Hide details' : 'View details'}
                </button>
                <button onClick={() => handleEditClick(record)} className="text-blue-600 text-sm hover:underline">Edit</button>
                <button onClick={() => handleDelete(record)} className="text-red-600 text-sm hover:underline">Delete</button>
              </div>
            </div>

            {expandedId === record.id && (
              <div className="mt-3 pt-3 border-t text-sm text-gray-700">
                <p className="mb-2">{record.narrative}</p>
                <p><span className="font-medium">Before:</span> {record.status_before || 'N/A'}</p>
                <p><span className="font-medium">After:</span> {record.status_after || 'N/A'}</p>
                <p className="text-gray-400 mt-2">Recorded on {record.date_recorded}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default ImpactRecordPage;
